import React, { useEffect, useState } from "react";
import { maintenanceApi, ProcBatch, ProcImportResult } from "../../../api/maintenance";
import { errMsg } from "./procUtils";
import { Upload } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  onClose: () => void;
  onDone: (res: ProcImportResult) => void;
}

/**
 * Seed the pool from device_configuration.csv — the IDs already minted offline
 * and physically handed out.
 *
 * This is a reconciliation, not a blind insert: an ID that already exists as a
 * live device is imported already onboarded and linked to it. IDs matching
 * several devices (which is what the old-<id> rename workaround leaves behind)
 * are imported unlinked and reported, because guessing which record is the real
 * one is exactly the silent wrong answer this system exists to prevent.
 */
const ImportCsvForm: React.FC<Props> = ({ onClose, onDone }) => {
  const [batches, setBatches] = useState<ProcBatch[]>([]);
  const [batchId, setBatchId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useNotice("error", "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<ProcImportResult | null>(null);

  useEffect(() => {
    maintenanceApi
      .procGetBatches()
      .then((r) => setBatches(r.data))
      .catch(() => undefined);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError("Choose a CSV file first");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await maintenanceApi.procImportCsv(file, batchId ? Number(batchId) : undefined);
      setResult(res.data);
    } catch (err: any) {
      setError(errMsg(err, "Import failed"));
      setSaving(false);
    }
  };

  if (result) {
    return (
      <div className="maint-form-overlay" onClick={onClose}>
        <div className="maint-form-card maint-form-card--wide" onClick={(e) => e.stopPropagation()}>
          <div className="maint-form-header">
            <h3>Import complete</h3>
            <button className="maint-btn-icon" onClick={onClose}>
              ✕
            </button>
          </div>

          <div className="proc-banner good">
            Added <b>{result.added}</b> ID{result.added === 1 ? "" : "s"} to{" "}
            <b>{result.batch_name}</b>.
          </div>

          <div className="proc-kv" style={{ marginBottom: 12 }}>
            <span className="k">Linked to live devices</span>
            <span className="v">{result.linked_onboarded} (imported as onboarded)</span>
            <span className="k">Already in the pool</span>
            <span className="v">{result.skipped_existing} (skipped)</span>
            <span className="k">Ambiguous</span>
            <span className="v">{result.ambiguous_ids.length}</span>
          </div>

          {result.ambiguous_ids.length > 0 && (
            <div className="proc-banner warn">
              <div>
                <b>Needs a human:</b> these IDs match more than one live device, so they were
                imported unlinked rather than guessed —{" "}
                {result.ambiguous_ids.map((id) => (
                  <span key={id} className="proc-chip proc-mono">
                    {id}
                  </span>
                ))}
                <div className="proc-note" style={{ marginTop: 6 }}>
                  This is normally the result of an <code>old-&lt;id&gt;</code> rename. Decide which
                  record is the real one before linking it.
                </div>
              </div>
            </div>
          )}

          <div className="maint-form-actions">
            <button className="maint-btn-primary" onClick={() => onDone(result)}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="maint-form-overlay" onClick={onClose}>
      <div className="maint-form-card maint-form-card--wide" onClick={(e) => e.stopPropagation()}>
        <div className="maint-form-header">
          <h3>Import existing device IDs</h3>
          <button className="maint-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="proc-mini" style={{ marginTop: 0 }}>
          Seed the pool from <code>device_configuration.csv</code> — the IDs the offline generator
          already produced. IDs that are already live devices are linked to them automatically, and
          re-importing the same file is a no-op.
        </p>

        <form onSubmit={handleSubmit}>
          <label
            style={{
              border: "2px dashed #cfd3e8",
              borderRadius: 10,
              padding: 24,
              textAlign: "center",
              background: "#fafbff",
              cursor: "pointer",
              display: "block",
            }}
          >
            <Upload size={30} color="#8a8fd0" />
            <div style={{ marginTop: 8, fontSize: 13, color: file ? "#333" : "#888" }}>
              {file ? <b>{file.name}</b> : "Choose device_configuration.csv"}
            </div>
            <div className="proc-mini" style={{ marginTop: 4 }}>
              columns: Unique ID, QR Code URL, WebSocket URL
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>

          <label style={{ marginTop: 14 }}>
            Assign to batch
            <select value={batchId} onChange={(e) => setBatchId(e.target.value)}>
              <option value="">Default backfill batch (B-LEGACY-001)</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.manufacturer_name ? ` · ${b.manufacturer_name}` : ""}
                </option>
              ))}
            </select>
          </label>

          {error && <p className="maint-form-error">{error}</p>}

          <div className="maint-form-actions">
            <button type="button" className="maint-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="maint-btn-primary" disabled={saving || !file}>
              {saving ? "Importing…" : "Import"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportCsvForm;
